import { isToday } from 'date-fns';
import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import {
  useAccessToken,
  useWaitlistClosedText
} from 'reservations/account/providers/ConfigProvider';
import {
  useAcceptsReservations,
  useAcceptsWaitlist,
  usePartySizeRange
} from 'reservations/account/providers/ConfigProvider';
import WaitlistMessage from 'reservations/waitlist/components/WaitlistMessage';
import {
  EnabledStateEnum,
  type WaitlistAvailability,
  useWaitlistAvailabilityQuery
} from 'reservations/waitlist/queries/useWaitlistAvailabilityQuery';

interface WaitlistContextInterface
  extends Pick<WaitlistAvailability, 'waitCount' | 'partySize' | 'enabled'> {
  hasActiveOnlineWaitlist: boolean;
  isWithinProvider: boolean;
}

const WaitlistContext = createContext<WaitlistContextInterface | null>(null);

type WaitlistProviderProps = {
  slug: string;
  children?: ReactNode;
};

function WaitlistProvider({ slug, children }: WaitlistProviderProps) {
  const accessToken = useAccessToken();
  const { max } = usePartySizeRange();
  const isAcceptingReservations = useAcceptsReservations();
  const isAcceptingWaitlist = useAcceptsWaitlist();
  const onlyAcceptingWaitlist = isAcceptingWaitlist && !isAcceptingReservations;
  const waitlistClosedText = useWaitlistClosedText();
  const [date, partySize] = useWatch({ name: ['date', 'partySize'] });
  const isCurrentDateToday = useMemo(() => isToday(date), [date]);
  const isQueryEnabled =
    !!slug && isAcceptingWaitlist && isCurrentDateToday && partySize <= max;

  const { data, isPending, isError } = useWaitlistAvailabilityQuery({
    slug,
    apiKey: accessToken,
    partySize,
    enabled: isQueryEnabled
  });

  // If the conditions aren't correct for a query, then this provider serves as a pass-through component
  if (!isQueryEnabled) {
    return <>{children}</>;
  }

  if (isPending) {
    return <></>;
  }

  // istanbul ignore next
  if (isError) {
    console.error('[WaitlistProvider]: Something went wrong here...');

    return (
      <WaitlistContext.Provider
        value={{
          isWithinProvider: true,
          hasActiveOnlineWaitlist: false,
          waitCount: 0,
          partySize: 0,
          enabled: false
        }}
      >
        {children}
      </WaitlistContext.Provider>
    );
  }

  const { enabled, enabledState, waitCount } = data;
  const hasActiveOnlineWaitlist = enabled && waitCount > 0;

  // NOTE: These error UIs should only render if we're away from the location list screen
  // and if we're dealing with the waitlist only variant.
  if (onlyAcceptingWaitlist) {
    // TODO: Need to confirm that this will be the same enabledState for book app waitlist temp disable
    if (
      enabledState === EnabledStateEnum.enum.Disabled ||
      enabledState === EnabledStateEnum.enum.Nowait
    ) {
      return (
        <WaitlistMessage
          dataTestId="waitlist-only-closed-with-active-shift"
          message={waitlistClosedText}
        />
      );
    }

    if (!enabled) {
      return (
        <WaitlistMessage
          dataTestId="waitlist-only-no-active-shift"
          message="The waitlist is currently closed"
        />
      );
    }
  }

  return (
    <WaitlistContext.Provider
      value={{
        isWithinProvider: true,
        hasActiveOnlineWaitlist,
        waitCount: waitCount,
        partySize: data.partySize,
        enabled: enabled
      }}
    >
      {children}
    </WaitlistContext.Provider>
  );
}

export function useWaitlist() {
  const context = useContext(WaitlistContext);
  if (context == null) {
    // NOTE: We don't want to throw an error here if we use this hook out of context
    // because this will let us know whether we are allowed to render the button
    // in situations where the WaitlistProvider is actually not part of the DOM.
    return {
      hasActiveOnlineWaitlist: false,
      isWithinProvider: false,
      waitCount: 0,
      partySize: 0,
      enabled: false
    };
  }

  // istanbul ignore next
  return context;
}

export default WaitlistProvider;
